import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { AttendanceResponse } from "../contracts/AttendanceResponse";
import { AttendanceBatchItem } from "../contracts/AttendanceBatchRequest";
import { outcomesToEntries, responseToAttendance } from "../mappers/attendanceMapper";
import { visitsKey, type OccurrenceAttendance, type SessionVisits } from "../visits-slice";

/** Load the saved attendance for one session occurrence. */
export const fetchSessionAttendance = createAsyncThunk<
  { key: string; attendance: OccurrenceAttendance },
  { bookingId: string; occurrenceDate: string },
  { rejectValue: ApiErrorResponse }
>("visits/fetch", async ({ bookingId, occurrenceDate }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<AttendanceResponse>(
      `/bookings/${bookingId}/attendance`,
      { params: { occurrenceDate } }
    );
    return {
      key: visitsKey(bookingId, occurrenceDate),
      attendance: responseToAttendance(response.data),
    };
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load attendance"));
  }
});

/** Persist (full replace) the attendance for one session occurrence. */
export const saveSessionAttendance = createAsyncThunk<
  { key: string; attendance: OccurrenceAttendance },
  {
    bookingId: string;
    occurrenceDate: string;
    outcomes: SessionVisits;
    oneOffMemberIds?: string[];
  },
  { rejectValue: ApiErrorResponse }
>(
  "visits/save",
  async ({ bookingId, occurrenceDate, outcomes, oneOffMemberIds }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put<AttendanceResponse>(
        `/bookings/${bookingId}/attendance`,
        {
          occurrenceDate,
          entries: outcomesToEntries(outcomes, new Set(oneOffMemberIds ?? [])),
        }
      );
      return {
        key: visitsKey(bookingId, occurrenceDate),
        attendance: responseToAttendance(response.data),
      };
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to save attendance"));
    }
  }
);

/** Load attendance for many session occurrences at once (for list/home card summaries). */
export const fetchAttendanceBatch = createAsyncThunk<
  Record<string, OccurrenceAttendance>,
  AttendanceBatchItem[],
  { rejectValue: ApiErrorResponse }
>("visits/fetchBatch", async (items, { rejectWithValue }) => {
  try {
    if (items.length === 0) return {};
    const response = await axiosInstance.post<AttendanceResponse[]>("/attendance/batch", {
      items,
    });
    const map: Record<string, OccurrenceAttendance> = {};
    for (const result of response.data) {
      map[visitsKey(result.bookingId, result.occurrenceDate)] = responseToAttendance(result);
    }
    return map;
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load attendance"));
  }
});
